<script>
	import { createEventDispatcher } from "svelte";
	import { fade } from "svelte/transition";
	import { data, riskLevels } from "./store";
	import Card from "./components/Card.svelte";
	import Note from "./components/Note.svelte";
	import RiskBar from "./components/RiskBar.svelte";
	import Risk from "./components/Risk.svelte";
	import ImagingFinding from "./components/ImagingFinding.svelte";

	const dispatcher = createEventDispatcher();
	$: score =
		$data.initialGcsScore > 0 ? $data.initialGcsScore : "Not provided";

	let risk = 0;

	/**
	 * if the patient has a depressed skull fracture ("Depressed_Fx=1") or midline shift ("Finding9=1")
	 * 	then Risk_level=5.
	 * If not, then if the patient has an epidural hematoma ("Finding6=1")
	 * 	then risk_level=4.
	 * If not, then if the GCS score is < 15 (GCSTotal=13 or GCSTotal=14)
	 * 	then risk_level=3.
	 * If not, then if the patient has a subdural hematoma ("Finding13=1") OR a contusion ("contusionICH=1") OR an extra-axial hematoma ("Finding7=1")
	 * 	then risk_level=2.
	 * If no risk factors are present,
	 * 	then risk_level=1.
	 */
	$: {
		if ($data.largeDepressedFracture === 'Yes' || $data.midlineShift === 'Yes') risk = 5;
		else if ($data.epiduralHematoma) risk = 4;
		else if ($data.initialGcsScore < 15 && $data.initialGcsScore > 0)
			risk = 3;
		else if (
			$data.subduralHematoma ||
			$data.cerebralContusion === "Yes" ||
			$data.extraAxialHematoma === 'Yes'
		)
			risk = 2;
		else risk = 1;
	}
	$: console.log($data)
	$: console.log(risk)
	$: riskLevel = riskLevels.find((x) => x.level == risk);
	$: progressWidth = ((riskLevel.overall * 100) / 40);
	
	const formatPercent = (value) =>
		value.toLocaleString("en", {
			style: "percent",
			minimumFractionDigits: 2,
		});

	// const neurosurgicalInterventionRisk = formatPercent(riskLevel.neurosurgicalInterventionRisk)
	// const intubationRisk = formatPercent(riskLevel.intubationRisk)
	// const deathRisk = formatPercent(riskLevel.deathRisk)
 </script>

<style>
	.findings h6 {
		font-weight: bold;
	}
	:global(.iq-card .iq-card-header) {
		min-height: auto;
	}
	:global(.progress-bar) {
		box-shadow: inset 0px -1px 0px 1px #00000036;
	}
	:global(.card) {
		font-size: 1em;
	}
	:global(strong) {
		font-size: 1em;
		font-weight: 600;
	}
</style>

<div id="findings" class="findings" in:fade>
	<Card title="Your Patient's Findings">
		<hr class="mt-1" />
		<div>
			<h5 class="d-inline-block mr-1 mt-1">GCS Score: <strong>{score}</strong></h5>
		</div>
		<h6 class="mt-1">Imaging findings:</h6>
		<ul class="mt-1 list-group">
			<ImagingFinding 
				label="Significantly depressed fracture"
				value={$data.largeDepressedFracture} />
			<ImagingFinding
				label="Epidural hematoma"
				value={$data.extraAxialHematoma === 'No' ? 'No' : $data.epiduralHematoma ? "Yes" : "No"}
			/>
			<ImagingFinding
				label="Subdural hematoma"
				value={$data.extraAxialHematoma === 'No' ? 'No' : $data.subduralHematoma ? "Yes" : "No"}
			/>
			<ImagingFinding
				label="Extra-axial hematoma"
				value={$data.extraAxialHematoma === 'Yes' && $data.unsureHematoma ? 'Yes' : 'No'} />
			<ImagingFinding
				label="Cerebral contusion"
				value={$data.cerebralContusion} />
			<ImagingFinding label="Midline shift" value={$data.midlineShift} />
			<ImagingFinding label="Subarachnoid Hemorrhage" value={$data.subarachnoidHemorrhage} />
		</ul>
		<button
			class="btn btn-primary btn-block mt-1"
			style="border-radius: 0;"
			on:click={() => dispatcher('goback')}>
			Edit Selections
		</button>
	</Card>
	<Card
		title="Predicted Neurological Risk"
		subtitle="(Neurosurgery, Intubation > 24 hours due to TBI, Death due to TBI)">
		<hr class="mt-1" />
		<div class="mb-3">
			<h6>Overall Risk</h6>
			<div>
				<Risk value={riskLevel.overall} />
			</div>
		</div>
		<h6>Risk Breakdown</h6>
		<ul class="list-group">
			<li class="list-group-item">
				<strong>{formatPercent(riskLevel.neurosurgicalInterventionRisk) === "0.10%" ? "< 0.10%" : formatPercent(riskLevel.neurosurgicalInterventionRisk)}</strong>
				risk of neurosurgerical intervention
			</li>
			<li class="list-group-item">
				<strong>{formatPercent(riskLevel.intubationRisk) === "0.10%" ? "< 0.10%" : formatPercent(riskLevel.intubationRisk)}</strong>
				risk of prolonged intubation due to TBI
			</li>
			<li class="list-group-item">
				<strong>{formatPercent(riskLevel.deathRisk) === "0.10%" ? "< 0.10%" : formatPercent(riskLevel.deathRisk)}</strong>
				risk of death due to TBI
			</li>
		</ul>
	</Card>
	<Card 
		title="Your Institution's Recommendation"
		instiRecommendation={true}
	>
		<hr class="mt-0" />
		<div class="mb-3">
			<strong>
				{#if risk <= 2}
					Every 4 hour neurological checks (i.e. Q4 hr)
				{:else if risk === 3}
					Either every hour or every 4 hour neurological checks
					acceptable
				{:else}Every 1 hour neurological checks (i.e. Q1 hr){/if}
			</strong>
		</div>
		<div
			style="width: {formatPercent(progressWidth + 0.1)}; text-align: right;">
			<span style="white-space: nowrap;">Your Patient</span>
		</div>

		<div style="margin-left: {(progressWidth * 100) - 1.4}%" data-icon="k" class="icon" />

		<RiskBar maxValue="40" greenLabel={`Q4hr`} yellowLabel="Q1 vs Q4 hrs" redLabel="Q1 hr" />
	</Card>
	<Card>
		<Note>
			For further information on the risk model development and
			validation, see the following publication:
			<div slot="footer">
				<a href="https://google.com" target="_blank">Title of the
					article</a>
				<cite title="Source Title">Journal Title</cite>
			</div>
		</Note>
	</Card>
	<button
		on:click={() => dispatcher('goback')}
		class="btn btn-primary btn-block"
		style="border-radius: 0;">
		Go Back
	</button>
</div>
